import { useCallback, useEffect, useMemo, useState } from "react";

import isEqual from "lodash/isEqual";

import useAllocationDndStore from "./useAllocationDnd";
import { formStoreManager } from "./useAllocationFormState";

type AllocationStoreMap = {
  [key: string]: { isValid: boolean; isDirty: boolean; isFieldsDirty: boolean };
};

const useAllocationSaveValidation = ({ isEdit }: { isEdit: boolean | undefined }) => {
  const [isRuleSelected, setIsRuleSelected] = useState(false);
  const {
    items,
    itemsOriginal,
    hasUnallocated,
    isGroup,
    fieldErrors,
    unallocatedCostsOriginal,
    unallocatedCosts,
    hasUnallocatedOriginal,
  } = useAllocationDndStore();
  const unallocatedDirty = unallocatedCostsOriginal !== unallocatedCosts || hasUnallocated !== hasUnallocatedOriginal;
  const [formValidationsMap, setFormValidationsMap] = useState<{
    [key: string]: { isValid: boolean; isDirty: boolean; isFieldsDirty: boolean };
  }>({});

  const isDirty =
    Object.keys(formValidationsMap).some((key) => formValidationsMap[key].isDirty) ||
    !isEqual(itemsOriginal, items) ||
    unallocatedDirty;

  const isFieldsDirty = Object.keys(formValidationsMap).some((key) => formValidationsMap[key].isFieldsDirty);

  const isError =
    Object.keys(formValidationsMap).some((key) => !formValidationsMap[key].isValid) ||
    Object.keys(fieldErrors).length > 0;

  // Check if allocation is single, if yes then on edit change to Save as flow
  const isSaveAs = useMemo(
    () => !isGroup && (items.main.length > 1 || hasUnallocated) && isEdit,
    [isGroup, items.main.length, isEdit, hasUnallocated]
  );
  const isDuplicate = useMemo(
    () => items.main.length < 2 && !hasUnallocated && !isEdit && isRuleSelected,
    [items.main.length, hasUnallocated, isEdit, isRuleSelected]
  );

  const getRuleStoreErrors = useCallback((items: string[]) => {
    const formStoresMap = items.reduce(
      (acc: AllocationStoreMap, item: string) => ({
        ...acc,
        [item]: {
          isValid: formStoreManager.getStore(item).getState().isValid,
          isDirty: formStoreManager.getStore(item).getState().isDirty,
          isFieldsDirty: formStoreManager.getStore(item).getState().isFieldsDirty,
        },
      }),
      {}
    );
    setFormValidationsMap(formStoresMap);
  }, []);

  const subscribeToStores = useCallback((items: string[]) => {
    const subs: (() => void)[] = items.map((item) =>
      formStoreManager.getStore(item).subscribe(({ isValid, isDirty, selectedExistingRule, isFieldsDirty }) => {
        setFormValidationsMap((prev) => ({ ...prev, [item]: { isValid, isDirty, isFieldsDirty } }));
        if (items.length < 2) {
          setIsRuleSelected(!!selectedExistingRule);
        }
      })
    );
    return subs;
  }, []);

  // Check if there are any errors in the form, if yes disable the save button
  useEffect(() => {
    // In case when we delete instance of rule we need to trigger check on items change
    if (items.main.length) {
      getRuleStoreErrors(items.main);
      const subs = subscribeToStores(items.main);
      return () => {
        subs.forEach((sub) => {
          sub();
        });
      };
    }
  }, [getRuleStoreErrors, items.main, items.main.length, subscribeToStores]);
  return {
    isError,
    isFieldsDirty,
    isSaveAs,
    isDirty,
    isDuplicate,
  };
};

export default useAllocationSaveValidation;
